// src/app/services/merchant-type.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { LogService } from './log.service';
import {
  FixedMerchantData,
  ItinerantMerchantData,
  ClusterData,
  EstimatedEconomies,
  Affluence,
  Coverage,
  TeamSize,
  MerchantType,
} from '../models/onboarding.model';
import { ClusterService } from './cluster.service';
import {
  ClusterMarchand,
  ClusterGenerique,
  ActivityType,
} from '../models/cluster.model';

@Injectable({
  providedIn: 'root',
})
export class MerchantTypeService {
  private apiUrl = '/api/clusters';
  private log = inject(LogService);

  constructor(
    private http: HttpClient,
    private clusterService: ClusterService,
  ) {}

  // ============================================
  // Obtenir cluster Fixed depuis backend
  // ============================================
  getCluster(merchantData: FixedMerchantData): Observable<ClusterData> {
    // Utiliser le cluster générique
    return this.getClusterFromGeneric(
      merchantData.activityType as string as ActivityType,
    );
  }

  // ============================================
  // Obtenir cluster Itinerant depuis backend
  // ============================================
  getClusterItinerant(
    merchantData: ItinerantMerchantData,
  ): Observable<ClusterData> {
    // Utiliser le cluster générique
    return this.getClusterFromGeneric(
      merchantData.activityType as string as ActivityType,
    );
  }

  // ============================================
  // Générer l'ID cluster exact selon tes conventions DB
  // ============================================
  private generateClusterIdFixed(data: FixedMerchantData): string {
    if (!data.merchantType || !data.activityType) {
      throw new Error(
        'merchantType ou activityType manquant pour générer le clusterId',
      );
    }

    const parts = [
      data.merchantType,
      data.activityType,
      'Déjeuner', // Service du midi
      this.mapAffluenceToCode(data.affluence),
      this.mapCoverageToCode(data.coverage),
      this.mapTeamSizeToCode(data.teamSize),
      data.periodicity ?? 'daily',
    ];
    return parts.join('_');
  }

  private mapAffluenceToCode(aff: Affluence): string {
    switch (aff) {
      case Affluence.LOW:
        return 'low';
      case Affluence.MEDIUM:
        return 'medium';
      case Affluence.HIGH:
        return 'high';
      default:
        return 'medium';
    }
  }

  private mapCoverageToCode(coverage: Coverage): string {
    switch (coverage) {
      case Coverage.COVERED:
        return 'covered';
      case Coverage.SEMI_COVERED:
        return 'semi-covered';
      case Coverage.UNCOVERED:
        return 'open';
    }
  }

  private mapTeamSizeToCode(teamSize: TeamSize): string {
    switch (teamSize) {
      case TeamSize.SOLO:
        return 'solo';
      case TeamSize.TWO_PERSONS:
        return '2persons';
      case TeamSize.THREE_PLUS:
        return '3plus';
      case TeamSize.MULTI_STAND:
        return 'multi_stand';
    }
  }

  // ============================================
  // Mapper ClusterGénérique → ClusterData
  // ============================================
  private getClusterFromGeneric(
    activityType: ActivityType,
  ): Observable<ClusterData> {
    return this.clusterService.getGenericCluster(activityType).pipe(
      map((generic) => this.mapGenericToClusterData(generic, activityType)),
      catchError((err) => {
        this.log.warn('Cluster indisponible, valeurs par défaut');
        return throwError(() => err);
      }),
    );
  }

  /**
   * Mappe un ClusterGénérique → format ClusterData compatible
   * Les données financières sont dérivées du catalogue
   */
  private mapGenericToClusterData(
    generic: ClusterGenerique,
    activityType: ActivityType,
  ): ClusterData {
    const catalogue = generic.catalogueOverrides || [];

    // Calculer les moyennes financières depuis le catalogue
    const avgPurchasePrice =
      catalogue.length > 0
        ? catalogue.reduce((sum, p) => sum + (p.basePurchasePrice || 0), 0) /
          catalogue.length
        : 2.5;
    const avgSellPrice =
      catalogue.length > 0
        ? catalogue.reduce((sum, p) => sum + (p.baseSellPrice || 0), 0) /
          catalogue.length
        : 5.0;
    const avgQuantity =
      catalogue.length > 0
        ? catalogue.reduce((sum, p) => sum + (p.baseQuantity || 0), 0) /
          catalogue.length
        : 50;

    // Valeurs par défaut réalistes pour waste/rupture (DB a 0, ces valeurs sont l'hypothèse industrie)
    const baseline = generic.performanceBaseline || ({} as any);
    const avgWastePercent =
      baseline.avgWastePercent > 0 ? baseline.avgWastePercent : 6;
    const avgRuptureFrequency =
      baseline.avgRuptureFrequency > 0 ? baseline.avgRuptureFrequency : 0.4;

    return {
      id: `cluster_${activityType}`,
      merchantType: MerchantType.FIXED,
      activityType: activityType as any,
      coverage: Coverage.SEMI_COVERED,
      teamSize: TeamSize.TWO_PERSONS,
      avgPreparedQuantity: avgQuantity,
      avgSoldQuantity: Math.round(avgQuantity * 0.92),
      avgWastePercent,
      avgRuptureFrequency,
      maxDebitPerHour: baseline.maxDebitPerHour || 2.5,
      hourlyDistribution: [],
      weatherAdjustments: {
        sunny: generic.weatherAdjustments?.sunny || 1.05,
        veryHot: generic.weatherAdjustments?.veryHot || 0.95,
        lightRain: generic.weatherAdjustments?.lightRain || 0.8,
        heavyRain: generic.weatherAdjustments?.heavyRain || 0.55,
        cold: generic.weatherAdjustments?.cold || 1.05,
        windy: generic.weatherAdjustments?.windy || 0.85,
      },
      dayOfWeekAdjustments: {
        monday: generic.dayOfWeekAdjustments?.monday || 0.95,
        tuesday: generic.dayOfWeekAdjustments?.tuesday || 0.95,
        wednesday: generic.dayOfWeekAdjustments?.wednesday || 1.0,
        thursday: generic.dayOfWeekAdjustments?.thursday || 1.0,
        friday: generic.dayOfWeekAdjustments?.friday || 1.1,
        saturday: generic.dayOfWeekAdjustments?.saturday || 1.05,
        sunday: generic.dayOfWeekAdjustments?.sunday || 0.8,
      },
      costPerUnit: Math.round(avgPurchasePrice * 100) / 100,
      sellingPrice: Math.round(avgSellPrice * 100) / 100,
      grossMargin: Math.round((1 - avgPurchasePrice / avgSellPrice) * 100),
      lostSaleValue: Math.round(avgSellPrice * 100) / 100,
      wasteValue: Math.round(avgPurchasePrice * 100) / 100,
    };
  }

  // ============================================
  // Estimer économies
  // ============================================
  estimateEconomies(
    merchantData: FixedMerchantData,
    cluster: ClusterData,
  ): EstimatedEconomies {
    // Hypothèses réalistes basées sur études sectorielles (ADEME, anti-gaspi)
    // Un commerçant sans outil de gestion gaspille généralement 15-25%
    const currentWastePercent = 18; // Gaspillage moyen sans outil
    const optimizedWastePercent = Math.max(3, cluster.avgWastePercent); // Avec notre outil

    const avgDailyPreparedQuantity = cluster.avgPreparedQuantity;
    const workingDaysPerMonth = 22;

    // 1️⃣ Réduction du gaspillage
    const currentWasteCost =
      (currentWastePercent / 100) *
      avgDailyPreparedQuantity *
      cluster.costPerUnit *
      workingDaysPerMonth;

    const optimizedWasteCost =
      (optimizedWastePercent / 100) *
      avgDailyPreparedQuantity *
      cluster.costPerUnit *
      workingDaysPerMonth;

    const wasteReduction = Math.max(0, currentWasteCost - optimizedWasteCost);

    // 2️⃣ Ventes manquées / ruptures évitées
    // En moyenne 2-3 ruptures par jour sans outil, chacune = ~1.5x valeur produit (panier abandonné)
    const dailyRuptures = Math.max(2, cluster.avgRuptureFrequency * 5);
    const avgBasketMultiplier = 1.8; // Impact panier (client qui part = vente + autres produits)
    const avoidedRuptures =
      dailyRuptures *
      workingDaysPerMonth *
      cluster.lostSaleValue *
      avgBasketMultiplier *
      0.6; // 60% des ruptures évitables avec prédiction

    // 3️⃣ Temps économisé (valorisé à 15€/h - SMIC chargé)
    const minutesSavedPerDay = 20; // Inventaire, commandes, planification
    const hourlyRate = 15;
    const timeSaved = minutesSavedPerDay * workingDaysPerMonth; // en minutes
    const timeValueSaved = (timeSaved / 60) * hourlyRate;

    const total = wasteReduction + avoidedRuptures + timeValueSaved;

    const saasPrice = 29.9;
    const roi = ((total - saasPrice) / saasPrice) * 100;

    return {
      wasteReduction: Math.round(wasteReduction),
      avoidedRuptures: Math.round(avoidedRuptures),
      timeSaved: Math.round(timeSaved),
      total: Math.round(total),
      roi,
    };
  }

  /**
   * Estimer les économies pour un marchand ITINÉRANT
   * @param merchantData - Données du marchand itinérant
   * @param cluster - Cluster récupéré depuis le backend
   */
  estimateEconomiesItinerant(
    merchantData: ItinerantMerchantData,
    cluster: ClusterData,
  ): EstimatedEconomies {
    // Hypothèses réalistes pour itinérants (événements, marchés, food trucks)
    // Le gaspillage sur événements est souvent plus élevé (incertitude météo, fréquentation)
    const currentWastePercent = 22; // Gaspillage moyen sans outil sur événements
    const optimizedWastePercent = Math.max(5, cluster.avgWastePercent);

    // Nombre d'événements par mois selon taille d'équipe
    let avgEventsPerMonth = 12; // Base: 3 événements/semaine (solo/petit)
    if (merchantData.teamSize === TeamSize.THREE_PLUS) avgEventsPerMonth = 16;
    if (merchantData.teamSize === TeamSize.MULTI_STAND) avgEventsPerMonth = 20;

    const avgQuantityPerEvent = cluster.avgPreparedQuantity;

    // 1️⃣ Réduction du gaspillage
    const currentWasteCost =
      (currentWastePercent / 100) *
      avgQuantityPerEvent *
      cluster.costPerUnit *
      avgEventsPerMonth;

    const optimizedWasteCost =
      (optimizedWastePercent / 100) *
      avgQuantityPerEvent *
      cluster.costPerUnit *
      avgEventsPerMonth;

    const wasteReduction = Math.max(0, currentWasteCost - optimizedWasteCost);

    // 2️⃣ Ventes manquées / ruptures évitées
    // Sur événement, une rupture = plusieurs ventes perdues (flux client intense)
    const rupturesPerEvent = Math.max(3, cluster.avgRuptureFrequency * 2);
    const avgBasketMultiplier = 2.0; // Impact panier plus fort sur événement
    const avoidedRuptures =
      rupturesPerEvent *
      avgEventsPerMonth *
      cluster.lostSaleValue *
      avgBasketMultiplier *
      0.5; // 50% évitables avec bonne prédiction

    // 3️⃣ Temps économisé pour planification (valorisé à 15€/h)
    const minutesSavedPerEvent = 35; // Préparation, inventaire, bilan
    const hourlyRate = 15;
    const timeSaved = minutesSavedPerEvent * avgEventsPerMonth; // en minutes
    const timeValueSaved = (timeSaved / 60) * hourlyRate;

    // Total
    const total = wasteReduction + avoidedRuptures + timeValueSaved;

    // ROI avec abonnement SaaS
    const saasPrice = 29.9;
    const roi = ((total - saasPrice) / saasPrice) * 100;

    return {
      wasteReduction: Math.round(wasteReduction),
      avoidedRuptures: Math.round(avoidedRuptures),
      timeSaved: Math.round(timeSaved),
      total: Math.round(total),
      roi: roi,
    };
  }

  // ============================================
  // ClusterMarchand — Délégation vers ClusterService
  // ============================================

  /**
   * Récupère le ClusterMarchand pour un marchand
   */
  getMerchantCluster(merchantId: string): Observable<ClusterMarchand | null> {
    return this.clusterService.getMerchantCluster(merchantId).pipe(
      catchError(() => {
        this.log.warn('ClusterMarchand introuvable');
        return of(null);
      }),
    );
  }

  /**
   * Crée un ClusterMarchand pendant l'onboarding
   */
  createMerchantCluster(
    activityType: ActivityType,
    positioning?: 'premium' | 'standard' | 'economy',
  ): Observable<ClusterMarchand | null> {
    return this.clusterService
      .createMerchantCluster({
        activityType,
        positioning: positioning ?? 'standard',
      })
      .pipe(
        catchError((err) => {
          this.log.warn('Impossible de créer ClusterMarchand', {
            error: (err as Error).message,
          });
          return of(null);
        }),
      );
  }

  // ============================================
  // Navigation onboarding
  // ============================================
  /**
   * Déterminer la prochaine étape selon le type de marchand
   */
  getNextStepRoute(currentStep: number, merchantType: MerchantType): string {
    if (merchantType === MerchantType.FIXED) {
      const routes = [
        '/onboarding/step/1',
        '/onboarding/step/2',
        '/onboarding/step/3',
        '/onboarding/step/4-fixed',
        '/onboarding/step/5',
        '/onboarding/step/6',
      ];
      return routes[currentStep] || '/onboarding/step/1';
    } else {
      const routes = [
        '/onboarding/step/1',
        '/onboarding/step/2',
        '/onboarding/step/3',
        '/onboarding/step/4-itinerant',
        '/onboarding/step/5',
        '/onboarding/step/6',
      ];
      return routes[currentStep] || '/onboarding/step/1';
    }
  }
}
